import React, { useState, useEffect, useMemo, useCallback, memo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { DashboardLayout } from '../../components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Switch } from '../../components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../../components/ui/alert-dialog';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  Plus, 
  Pencil, 
  Trash2, 
  Package, 
  Search,
  Loader2,
  GripVertical,
  Hash,
  Eye,
  EyeOff,
  ArrowLeft,
  Check,
  MoreVertical,
  FileText,
  Upload,
  X as XIcon,
  BookOpen,
  Image as ImageIcon,
  ShieldCheck,
  Link as LinkIcon,
} from 'lucide-react';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  TouchSensor,
  useSensor,
  useSensors,
  DragOverlay,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

import { BackshopDuplikatDialog } from '../../components/BackshopDuplikatDialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ─── Produkt-Labels Konfiguration ─────────────────────────────────────────────
export const PRODUCT_LABELS = [
  { key: 'kernsortiment',  label: 'Kernsortiment',    color: '#3b82f6', bg: '#eff6ff', border: '#bfdbfe' },
  { key: 'dummy',          label: 'Dummy-Sortiment',  color: '#ca8a04', bg: '#fefce8', border: '#fde68a' },
  { key: 'aktion',         label: 'Aktion',            color: '#ef4444', bg: '#fef2f2', border: '#fecaca' },
  { key: 'korbaktion',     label: 'Korbaktion',        color: '#22c55e', bg: '#f0fdf4', border: '#bbf7d0' },
  { key: 'backwaren',      label: 'Backwarenangebot',  color: '#f97316', bg: '#fff7ed', border: '#fed7aa' },
];

// Sortable Product Item Component
const SortableProductItem = memo(({ product, categories, onEdit, onDelete, onToggleActive, onCategoryChange }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
    setActivatorNodeRef,
  } = useSortable({ id: product.id });

  const [showActions, setShowActions] = useState(false);

  const style = useMemo(() => ({
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  }), [transform, transition, isDragging]);

  const isActive = product.active !== false;

  // Single drag handle component
  const DragHandle = () => (
    <button
      type="button"
      ref={setActivatorNodeRef}
      {...listeners}
      {...attributes}
      className="cursor-grab active:cursor-grabbing touch-none hover:bg-gray-200 rounded flex-shrink-0"
      style={{ width: '32px', height: '32px', padding: '6px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
      aria-label="Produkt verschieben"
    >
      <GripVertical style={{ width: '20px', height: '20px', flexShrink: 0, color: '#6b7280' }} />
    </button>
  );

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`relative p-4 hover:bg-gray-50 dark:hover:bg-slate-700/40 border-b last:border-b-0 ${
        isDragging ? 'bg-orange-50 dark:bg-orange-900/20 shadow-lg rounded-lg z-50' : ''
      } ${!isActive ? 'bg-gray-100 dark:bg-slate-800/50 opacity-60' : ''}`}
    >
      {/* Mobile Layout (< 768px) */}
      <div className="flex flex-col gap-3 md:hidden">
        {/* Row 1: Drag Handle, Package Icon, Product Name, 3-Dot Menu */}
        <div className="flex items-start gap-3">
          <DragHandle />
          <Package className={`h-5 w-5 mt-1 ${isActive ? 'text-gray-400' : 'text-gray-300'}`} />
          <div className="flex-1">
            <div className="flex items-center gap-2 flex-wrap">
              <span className={`font-medium text-base ${!isActive ? 'text-gray-400 line-through' : 'text-slate-900 dark:text-slate-100'}`}>
                {product.name}
              </span>
              {!isActive && (
                <Badge variant="secondary" className="text-xs bg-gray-200 text-gray-500">
                  <EyeOff className="h-3 w-3 mr-1" />
                  Inaktiv
                </Badge>
              )}
              {product.image_from_master && (
                <span className="inline-flex items-center gap-0.5 text-[10px] bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400 border border-blue-200 dark:border-blue-700 px-1.5 py-0.5 rounded-full" title="Bild automatisch aus dem Master-Katalog übernommen">
                  <BookOpen className="h-2.5 w-2.5" /> Master
                </span>
              )}
            </div>
            {product.article_number && (
              <span className={`text-xs flex items-center gap-1 mt-1 ${isActive ? 'text-gray-500' : 'text-gray-400'}`}>
                <Hash className="h-3 w-3" />
                {product.article_number}
              </span>
            )}
          </div>
          {/* 3-Dot Menu Button */}
          <button
            type="button"
            onClick={() => setShowActions(!showActions)}
            className="p-2 hover:bg-gray-200 rounded-full"
          >
            <MoreVertical className="h-5 w-5 text-gray-600 dark:text-gray-400" />
          </button>
        </div>

        {/* Row 2: Category Dropdown */}
        <div>
          <select
            value={product.category_id}
            onChange={(e) => onCategoryChange(product.id, e.target.value)}
            disabled={!isActive}
            className={`w-full px-3 py-2 text-sm border-2 rounded-md ${
              isActive 
                ? 'border-orange-400 text-slate-900 dark:text-slate-100 bg-white dark:bg-slate-800 hover:border-orange-500' 
                : 'border-gray-300 text-gray-400 bg-gray-100 dark:bg-slate-700 cursor-not-allowed'
            }`}
          >
            {categories.map((category) => (
              <option key={category.id} value={category.id}>
                📁 {category.name}
              </option>
            ))}
          </select>
        </div>

        {/* Row 3: Active/Inactive Toggle */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Switch
              checked={isActive}
              onCheckedChange={() => onToggleActive(product)}
              className="data-[state=checked]:bg-green-500"
            />
            <span className={`text-sm font-medium ${isActive ? 'text-green-600' : 'text-gray-400'}`}>
              {isActive ? '🟢 Aktiv' : '⚫ Inaktiv'}
            </span>
          </div>
        </div>

        {/* Actions Menu (Collapsible) */}
        {showActions && (
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                onEdit(product);
                setShowActions(false);
              }}
              className="flex-1 text-blue-600 border-blue-300 hover:bg-blue-50 dark:hover:bg-blue-900/20"
            >
              <Pencil className="h-4 w-4 mr-2" />
              Bearbeiten
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                onDelete(product);
                setShowActions(false);
              }}
              className="flex-1 text-red-600 border-red-300 hover:bg-red-50 dark:hover:bg-red-900/20"
            >
              <Trash2 className="h-4 w-4 mr-2" />
              Löschen
            </Button>
          </div>
        )}
      </div>

      {/* Desktop Layout (>= 768px) - Original Design */}
      <div className="hidden md:flex items-center justify-between">
        <div className="flex items-center gap-3 flex-1">
          <DragHandle />
          <Package className={`h-5 w-5 ${isActive ? 'text-gray-400' : 'text-gray-300'}`} />
          <div className="flex flex-col flex-1">
            <div className="flex items-center gap-2">
              <span className={`font-medium ${!isActive ? 'text-gray-400 line-through' : 'text-slate-900 dark:text-slate-100'}`}>
                {product.name}
              </span>
              {!isActive && (
                <Badge variant="secondary" className="text-xs bg-gray-200 text-gray-500">
                  <EyeOff className="h-3 w-3 mr-1" />
                  Inaktiv
                </Badge>
              )}
            </div>
            {product.article_number && (
              <span className={`text-xs flex items-center gap-1 ${isActive ? 'text-gray-500' : 'text-gray-400'}`}>
                <Hash className="h-3 w-3" />
                {product.article_number}
              </span>
            )}
          </div>
        </div>

        {/* Category Radio Buttons (Desktop) */}
        <div className="flex items-center gap-2 mr-4">
          {categories.map((category) => {
            const isSelected = product.category_id === category.id;
            return (
              <button
                key={category.id}
                onClick={() => onCategoryChange(product.id, category.id)}
                disabled={!isActive}
                className={`
                  relative px-3 py-1.5 text-sm rounded-md border-2 transition-all
                  ${isSelected 
                    ? 'bg-orange-500 text-white border-orange-600 font-medium shadow-sm' 
                    : 'bg-white dark:bg-slate-800 text-gray-600 dark:text-slate-300 border-gray-300 dark:border-slate-600 hover:border-orange-400 hover:text-orange-600'
                  }
                  ${!isActive ? 'opacity-40 cursor-not-allowed' : 'cursor-pointer'}
                `}
              >
                {isSelected && <Check className="h-3 w-3 inline mr-1" />}
                {category.name}
              </button>
            );
          })}
        </div>

        <div className="flex items-center gap-3">
          {/* Active/Inactive Toggle */}
          <div className="flex items-center gap-2">
            <Switch
              checked={isActive}
              onCheckedChange={() => onToggleActive(product)}
              className="data-[state=checked]:bg-green-500"
            />
            <span className={`text-xs ${isActive ? 'text-green-600' : 'text-gray-400'}`}>
              {isActive ? 'Aktiv' : 'Inaktiv'}
            </span>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => onEdit(product)}
          >
            <Pencil className="h-4 w-4 text-gray-500" />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => onDelete(product)}
            className="text-red-500 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-900/20"
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </div>
  );
});

export const BackshopAlleProdukte = () => {
  const navigate = useNavigate();
  const [categories, setCategories] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeProduct, setActiveProduct] = useState(null);
  
  // Product Dialog
  const [productDialogOpen, setProductDialogOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [productName, setProductName] = useState('');
  const [productArticleNumber, setProductArticleNumber] = useState('');
  const [productCategoryId, setProductCategoryId] = useState('');
  const [productActive, setProductActive] = useState(true);
  const [savingProduct, setSavingProduct] = useState(false);
  // Handbuch PDF-Felder
  const [productBeschreibung, setProductBeschreibung] = useState('');
  const [productLabels, setProductLabels] = useState([]);
  const [productPdfFile, setProductPdfFile] = useState(null);
  const [productCurrentThumbnailUrl, setProductCurrentThumbnailUrl] = useState(null);
  const [productCurrentPdfUrl, setProductCurrentPdfUrl] = useState(null);
  const pdfFileRef = useRef();
  // Vitrinenbild-Felder
  const [productImageFile, setProductImageFile] = useState(null);
  const [productCurrentImageUrl, setProductCurrentImageUrl] = useState(null);
  const imageFileRef = useRef();
  
  // Delete Dialog
  const [deleteProductDialog, setDeleteProductDialog] = useState(null);

  // ── Gatekeeper: Master-Katalog Check ────────────────────────────────────────
  const [gatekeeperMatch, setGatekeeperMatch] = useState(null);   // gefundenes Master-Produkt
  const [gatekeeperPendingPayload, setGatekeeperPendingPayload] = useState(null); // Original-Payload falls "Nein"
  const [gatekeeperLoading, setGatekeeperLoading] = useState(false);

  // DnD Sensors - Optimized for both desktop and mobile
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 5, // Reduced for better responsiveness
      },
    }),
    useSensor(TouchSensor, {
      activationConstraint: {
        delay: 150, // Slightly reduced delay
        tolerance: 8, // Increased tolerance for better mobile experience
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [catRes, prodRes] = await Promise.all([
        axios.get(`${API}/backshop/categories`),
        axios.get(`${API}/backshop/all-products`)
      ]);
      
      setCategories(catRes.data || []);
      setProducts(prodRes.data || []);
    } catch (error) {
      toast.error('Fehler beim Laden der Daten');
    } finally {
      setLoading(false);
    }
  };

  // Sort products: Active products first (by order), then inactive at bottom (by order)
  const sortedProducts = useMemo(() => {
    const active = products.filter(p => p.active !== false).sort((a, b) => (a.order || 0) - (b.order || 0));
    const inactive = products.filter(p => p.active === false).sort((a, b) => (a.order || 0) - (b.order || 0));
    return [...active, ...inactive];
  }, [products]);

  // Live-Suche: filtert nach Produktname + Artikelnummer (case-insensitive)
  const [search, setSearch] = useState('');
  const displayProducts = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return sortedProducts;
    return sortedProducts.filter(p =>
      (p.name || '').toLowerCase().includes(q) ||
      (p.article_number || '').toLowerCase().includes(q)
    );
  }, [sortedProducts, search]);

  const handleCategoryChange = async (productId, categoryId) => {
    try {
      await axios.put(`${API}/backshop/products/${productId}/category`, {
        category_id: categoryId
      });
      
      // Update local state
      setProducts(prev => prev.map(p => 
        p.id === productId ? { ...p, category_id: categoryId } : p
      ));
      
      toast.success('Kategorie aktualisiert');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Aktualisieren');
    }
  };

  const handleToggleActive = async (product) => {
    const newActiveState = !(product.active !== false);
    
    try {
      await axios.put(`${API}/backshop/products/${product.id}`, {
        name: product.name,
        category_id: product.category_id,
        article_number: product.article_number || '',
        order: product.order || 0,
        active: newActiveState
      });
      
      // Update local state
      setProducts(prev => prev.map(p => 
        p.id === product.id ? { ...p, active: newActiveState } : p
      ));
      
      toast.success(newActiveState ? 'Produkt aktiviert' : 'Produkt deaktiviert');
    } catch (error) {
      toast.error('Fehler beim Aktualisieren');
    }
  };

  const openAddProduct = () => {
    setEditingProduct(null);
    setProductName('');
    setProductArticleNumber('');
    setProductCategoryId(categories[0]?.id || '');
    setProductActive(true);
    setProductBeschreibung('');
    setProductLabels([]);
    setProductPdfFile(null);
    setProductCurrentThumbnailUrl(null);
    setProductCurrentPdfUrl(null);
    setProductImageFile(null);
    setProductCurrentImageUrl(null);
    setProductDialogOpen(true);
  };

  const openEditProduct = (product) => {
    setEditingProduct(product);
    setProductName(product.name);
    setProductArticleNumber(product.article_number || '');
    setProductCategoryId(product.category_id);
    setProductActive(product.active !== false);
    setProductBeschreibung(product.beschreibung || '');
    setProductLabels(product.labels || []);
    setProductPdfFile(null);
    setProductCurrentThumbnailUrl(product.thumbnail_url || null);
    setProductCurrentPdfUrl(product.pdf_url || null);
    setProductImageFile(null);
    setProductCurrentImageUrl(product.image_url || null);
    setProductDialogOpen(true);
  };

  const saveProduct = async (skipGatekeeper = false) => {
    if (!productName.trim()) {
      toast.error('Produktname ist erforderlich');
      return;
    }
    if (!productCategoryId) {
      toast.error('Kategorie ist erforderlich');
      return;
    }

    // ── AP3-Gatekeeper: nur bei NEUEM Produkt + Artikelnummer oder EAN vorhanden ──
    if (!editingProduct && (productArticleNumber.trim()) && !skipGatekeeper) {
      setGatekeeperLoading(true);
      try {
        const res = await axios.post(`${API}/backshop/check-duplicate`, {
          article_number: productArticleNumber.trim(),
          ean: '',
        }, { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });
        const { result, matches } = res.data;
        if (result === 'single_match' && matches?.length === 1) {
          // Eindeutiger Treffer → Vorausfüll-Dialog
          setGatekeeperMatch(matches[0]);
          setGatekeeperPendingPayload({
            name: productName.trim(),
            category_id: productCategoryId,
            article_number: productArticleNumber.trim(),
            active: productActive,
            labels: productLabels,
            beschreibung: productBeschreibung.trim(),
            imageFile: productImageFile,
            pdfFile: productPdfFile,
          });
          setGatekeeperLoading(false);
          return;
        }
        // multiple_matches → einfach anlegen; create_backshop_product füllt Merge-Queue
      } catch (_) { /* Gatekeeper-Fehler = silent skip */ }
      setGatekeeperLoading(false);
    }

    setSavingProduct(true);
    try {
      let savedProduct;
      if (editingProduct) {
        // Update Basisfelder
        const res = await axios.put(`${API}/backshop/products/${editingProduct.id}`, {
          name: productName.trim(),
          category_id: productCategoryId,
          article_number: productArticleNumber.trim(),
          order: editingProduct.order || 0,
          active: productActive,
          labels: productLabels,
        });
        savedProduct = res.data;
        toast.success('Produkt aktualisiert');
      } else {
        // Create
        const res = await axios.post(`${API}/backshop/products`, {
          name: productName.trim(),
          category_id: productCategoryId,
          article_number: productArticleNumber.trim(),
          order: 0,
          active: productActive,
          labels: productLabels,
        });
        savedProduct = res.data;
        toast.success('Produkt erstellt');
      }

      // PDF hochladen (falls ausgewählt)
      if (productPdfFile && savedProduct?.id) {
        const formData = new FormData();
        formData.append('beschreibung', productBeschreibung.trim());
        formData.append('pdf_file', productPdfFile);
        await axios.post(`${API}/backshop/products/${savedProduct.id}/pdf`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        toast.success('PDF hochgeladen');
      } else if (editingProduct && productBeschreibung.trim() !== (editingProduct.beschreibung || '')) {
        // Nur Beschreibung aktualisieren (ohne neues PDF)
        const fd = new FormData();
        fd.append('beschreibung', productBeschreibung.trim());
        await axios.patch(`${API}/backshop/products/${editingProduct.id}/beschreibung`, fd);
      }

      // Bild hochladen (falls ausgewählt)
      if (productImageFile && savedProduct?.id) {
        const formData = new FormData();
        formData.append('image_file', productImageFile);
        const imgRes = await axios.post(`${API}/backshop/products/${savedProduct.id}/image`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        if (imgRes.data?.media_suggestion_created) {
          toast.info('Bild hochgeladen. Verbesserungsvorschlag an den Superadmin gesendet.', { icon: '📸', duration: 4000 });
        } else {
          toast.success('Bild hochgeladen');
        }
      }

      // Szenario A Info: Bild wurde automatisch aus Master-Katalog übernommen
      if (!productImageFile && savedProduct?.image_from_master) {
        toast.info('Bild automatisch aus dem Master-Katalog übernommen', { icon: '🖼️', duration: 4000 });
      }

      setProductDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Speichern');
    } finally {
      setSavingProduct(false);
    }
  };


  // ── AP3-Gatekeeper: Übernehmen + optionale Asset-Anreicherung ────────────────
  const handleGatekeeperAccept = async () => {
    if (!gatekeeperMatch) return;
    setSavingProduct(true);
    try {
      await axios.post(`${API}/admin/master-products/${gatekeeperMatch.id}/accept`,
        { local_price: null },
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
      );
      toast.success(`"${gatekeeperMatch.product_name}" in dein Sortiment übernommen`);
      setGatekeeperMatch(null);
      setGatekeeperPendingPayload(null);
      setProductDialogOpen(false);
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Fehler beim Übernehmen');
    } finally {
      setSavingProduct(false);
    }
  };

  const handleGatekeeperEnrichImage = async (file) => {
    if (!gatekeeperMatch) return;
    try {
      await axios.post(`${API}/backshop/master/${gatekeeperMatch.id}/enrich`,
        file,
        { headers: { 'Content-Type': file.type, Authorization: `Bearer ${localStorage.getItem('token')}` } }
      );
      toast.success('Bild hochgeladen und im Katalog hinterlegt');
      setGatekeeperMatch(m => m ? { ...m, has_image: true } : m);
    } catch { toast.error('Fehler beim Bild-Upload'); }
  };

  const handleGatekeeperEnrichPdf = async (file) => {
    if (!gatekeeperMatch) return;
    try {
      await axios.post(`${API}/backshop/master/${gatekeeperMatch.id}/enrich`,
        file,
        { headers: { 'Content-Type': 'application/pdf', Authorization: `Bearer ${localStorage.getItem('token')}` } }
      );
      toast.success('PDF hochgeladen und im Katalog hinterlegt');
      setGatekeeperMatch(m => m ? { ...m, has_pdf: true } : m);
    } catch { toast.error('Fehler beim PDF-Upload'); }
  };

  // ── Gatekeeper: "Abbrechen" → lokal anlegen (skipGatekeeper) ─────────────────
  const handleGatekeeperDecline = () => {
    setGatekeeperMatch(null);
    setGatekeeperPendingPayload(null);
    saveProduct(true);
  };


  const deleteProduct = async () => {
    
    try {
      await axios.delete(`${API}/backshop/products/${deleteProductDialog.id}`);
      toast.success('Produkt gelöscht');
      setDeleteProductDialog(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Löschen');
    }
  };

  const handleDragStart = (event) => {
    const { active } = event;
    const product = products.find(p => p.id === active.id);
    setActiveProduct(product);
  };

  const handleDragEnd = async (event) => {
    const { active, over } = event;
    setActiveProduct(null);

    if (!over || active.id === over.id) return;

    const oldIndex = sortedProducts.findIndex(p => p.id === active.id);
    const newIndex = sortedProducts.findIndex(p => p.id === over.id);

    if (oldIndex === -1 || newIndex === -1) return;

    // Check if dragging within same group (active <-> active or inactive <-> inactive)
    const draggedProduct = sortedProducts[oldIndex];
    const targetProduct = sortedProducts[newIndex];
    const draggedIsActive = draggedProduct.active !== false;
    const targetIsActive = targetProduct.active !== false;

    if (draggedIsActive !== targetIsActive) {
      toast.error('Sie können nur innerhalb der gleichen Gruppe (Aktiv/Inaktiv) sortieren');
      return;
    }

    // Reorder within the same list
    const reordered = arrayMove(sortedProducts, oldIndex, newIndex);
    
    // Update all products with their new order indices
    const updatedProducts = products.map(product => {
      const newOrderIndex = reordered.findIndex(p => p.id === product.id);
      return {
        ...product,
        order: newOrderIndex !== -1 ? newOrderIndex : product.order || 0
      };
    });
    
    setProducts(updatedProducts);

    // Send new order to backend
    try {
      await axios.put(`${API}/backshop/products/batch/update-order`, {
        product_ids: reordered.map(p => p.id)
      });
      toast.success('Reihenfolge aktualisiert');
    } catch (error) {
      toast.error('Fehler beim Sortieren');
      // Revert on error
      fetchData();
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-96">
          <Loader2 className="h-12 w-12 animate-spin text-primary" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div className="flex-1">
            <div className="flex items-center gap-3 mb-2">
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={() => navigate('/admin/abschriften/backshop-liste')}
                className="text-gray-500 hover:text-gray-700 dark:text-gray-300"
              >
                <ArrowLeft className="h-4 w-4 mr-1" />
                Zurück
              </Button>
            </div>
            <h1 className="text-2xl sm:text-3xl font-bold" style={{fontFamily: 'Manrope, sans-serif'}}>
              Alle Produkte
            </h1>
            <p className="text-sm sm:text-base text-muted-foreground mt-2">
              Produkte verwalten und Kategorien schnell zuweisen
            </p>
          </div>
          <Button onClick={openAddProduct} className="bg-orange-600 hover:bg-orange-700 w-full sm:w-auto">
            <Plus className="h-4 w-4 mr-2" />
            Produkt hinzufügen
          </Button>
        </div>

        {/* Live-Suchleiste */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
          <Input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Produkt suchen (Name oder Artikelnummer)…"
            className="pl-9 pr-9 h-11"
            data-testid="backshop-produkt-suche"
          />
          {search && (
            <button
              type="button"
              onClick={() => setSearch('')}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
              data-testid="backshop-suche-clear"
              aria-label="Suche zurücksetzen"
            >
              <XIcon className="h-4 w-4" />
            </button>
          )}
        </div>

        {/* Stats Bar */}
        <Card className="bg-gradient-to-r from-orange-50 to-amber-50 border-orange-200 dark:from-orange-950/30 dark:to-amber-950/20 dark:border-orange-900/50">
          <CardContent className="py-4">
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 sm:gap-6">
              <div className="flex items-center gap-2">
                <Eye className="h-5 w-5 text-green-600 dark:text-green-500" />
                <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                  {products.filter(p => p.active !== false).length} aktive Produkte
                </span>
              </div>
              <div className="flex items-center gap-2">
                <EyeOff className="h-5 w-5 text-gray-400 dark:text-gray-500" />
                <span className="text-sm text-gray-500 dark:text-gray-400">
                  {products.filter(p => p.active === false).length} inaktive Produkte
                </span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Products List */}
        {products.length === 0 ? (
          <Card className="border-2 border-dashed">
            <CardContent className="py-16 text-center">
              <Package className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <p className="text-lg font-medium">Keine Produkte vorhanden</p>
              <p className="text-muted-foreground mt-2">
                Erstellen Sie Ihr erstes Produkt
              </p>
            </CardContent>
          </Card>
        ) : (
          <Card>
            <CardHeader className="bg-gradient-to-r from-orange-500 to-orange-600 text-white py-3">
              <CardTitle className="flex items-center justify-between text-lg">
                <div className="flex items-center gap-2">
                  <Package className="h-5 w-5" />
                  Produktliste
                </div>
                <Badge variant="secondary" className="bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300" data-testid="backshop-produkt-count">
                  {search.trim() ? `${displayProducts.length} von ${products.length}` : `${products.length} Produkte`}
                </Badge>
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              {displayProducts.length === 0 ? (
                <div className="py-12 text-center" data-testid="backshop-suche-leer">
                  <Search className="h-10 w-10 text-muted-foreground mx-auto mb-3" />
                  <p className="font-medium">Keine Treffer für „{search}"</p>
                  <p className="text-sm text-muted-foreground mt-1">Anderen Namen oder Artikelnummer versuchen</p>
                </div>
              ) : (
              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragStart={handleDragStart}
                onDragEnd={handleDragEnd}
              >
                <SortableContext
                  items={displayProducts.map(p => p.id)}
                  strategy={verticalListSortingStrategy}
                >
                  <div className="divide-y">
                    {displayProducts.map((product) => (
                      <SortableProductItem
                        key={product.id}
                        product={product}
                        categories={categories}
                        onEdit={openEditProduct}
                        onDelete={setDeleteProductDialog}
                        onToggleActive={handleToggleActive}
                        onCategoryChange={handleCategoryChange}
                      />
                    ))}
                  </div>
                </SortableContext>
                <DragOverlay>
                  {activeProduct && (
                    <div className="flex items-center gap-3 p-4 bg-white dark:bg-gray-900 shadow-xl rounded-lg border-2 border-orange-400">
                      <GripVertical className="h-5 w-5 text-gray-400" />
                      <Package className="h-5 w-5 text-orange-500" />
                      <span className="font-medium">{activeProduct.name}</span>
                    </div>
                  )}
                </DragOverlay>
              </DndContext>
              )}
            </CardContent>
          </Card>
        )}
      </div>

      {/* Product Dialog */}
      <Dialog open={productDialogOpen} onOpenChange={setProductDialogOpen}>
        <DialogContent className="max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingProduct ? 'Produkt bearbeiten' : 'Neues Produkt'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label htmlFor="product-name">Produktname *</Label>
              <Input
                id="product-name"
                value={productName}
                onChange={(e) => setProductName(e.target.value)}
                placeholder="z.B. Croissant"
              />
            </div>
            <div>
              <Label htmlFor="article-number">Artikelnummer</Label>
              <Input
                id="article-number"
                value={productArticleNumber}
                onChange={(e) => setProductArticleNumber(e.target.value)}
                placeholder="z.B. 1234"
              />
            </div>
            <div>
              <Label htmlFor="category">Kategorie *</Label>
              <select
                id="category"
                value={productCategoryId}
                onChange={(e) => setProductCategoryId(e.target.value)}
                className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2"
              >
                {categories.map(cat => (
                  <option key={cat.id} value={cat.id}>{cat.name}</option>
                ))}
              </select>
            </div>
            <div className="flex items-center gap-2">
              <Switch
                id="product-active"
                checked={productActive}
                onCheckedChange={setProductActive}
              />
              <Label htmlFor="product-active">Aktiv</Label>
            </div>

            {/* ── Sortiments-Labels ── */}
            <div className="border-t pt-3 space-y-2">
              <Label className="text-sm font-medium">Sortiments-Zuordnung <span className="text-muted-foreground font-normal">(mehrere möglich)</span></Label>
              <div className="flex flex-wrap gap-2">
                {PRODUCT_LABELS.map(lbl => {
                  const active = productLabels.includes(lbl.key);
                  return (
                    <button
                      key={lbl.key}
                      type="button"
                      onClick={() => setProductLabels(prev =>
                        active ? prev.filter(k => k !== lbl.key) : [...prev, lbl.key]
                      )}
                      className="flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium border-2 transition-all"
                      style={{
                        borderColor: lbl.color,
                        backgroundColor: active ? lbl.color : lbl.bg,
                        color: active ? '#fff' : lbl.color,
                      }}
                      data-testid={`label-toggle-${lbl.key}`}
                    >
                      <span className="w-2 h-2 rounded-full" style={{ backgroundColor: active ? '#fff' : lbl.color }} />
                      {lbl.label}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* ── Handbuch-Bereich ── */}
            <div className="border-t pt-3 space-y-3">
              <div className="flex items-center gap-2 text-sm font-medium text-amber-700 dark:text-amber-400">
                <BookOpen className="h-4 w-4" />
                Handbuch (optional)
              </div>
              <div>
                <Label htmlFor="beschreibung">Interne Beschreibung</Label>
                <Input
                  id="beschreibung"
                  value={productBeschreibung}
                  onChange={(e) => setProductBeschreibung(e.target.value)}
                  placeholder="Optionale Notiz für das Handbuch..."
                />
              </div>
              <div>
                <Label>PDF-Rezept</Label>
                {productCurrentPdfUrl && !productPdfFile && (
                  <div className="flex items-center gap-2 mb-2 text-sm text-green-700 dark:text-green-400">
                    <FileText className="h-4 w-4" />
                    <span>PDF vorhanden</span>
                    {productCurrentThumbnailUrl && (
                      <img
                        src={`${process.env.REACT_APP_BACKEND_URL}${productCurrentThumbnailUrl}?token=${localStorage.getItem('token')}`}
                        alt="Vorschau"
                        className="h-10 w-10 object-cover rounded border"
                        onError={(e) => { e.target.style.display = 'none'; }}
                      />
                    )}
                    <button
                      type="button"
                      onClick={async () => {
                        try {
                          await axios.delete(`${API}/backshop/products/${editingProduct.id}/pdf`);
                          setProductCurrentPdfUrl(null);
                          setProductCurrentThumbnailUrl(null);
                          toast.success('PDF entfernt');
                          fetchData();
                        } catch (e) {
                          toast.error('Fehler beim Entfernen');
                        }
                      }}
                      className="text-red-500 hover:text-red-700 ml-1"
                    >
                      <XIcon className="h-4 w-4" />
                    </button>
                  </div>
                )}
                <div
                  className="border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg p-3 text-center cursor-pointer hover:border-amber-400 transition-colors"
                  onClick={() => pdfFileRef.current?.click()}
                >
                  {productPdfFile ? (
                    <div className="flex items-center justify-center gap-2 text-sm">
                      <FileText className="h-4 w-4 text-amber-500" />
                      <span className="truncate max-w-[180px]">{productPdfFile.name}</span>
                      <button type="button" onClick={(e) => { e.stopPropagation(); setProductPdfFile(null); }}>
                        <XIcon className="h-3 w-3 text-gray-400 hover:text-red-500" />
                      </button>
                    </div>
                  ) : (
                    <div className="flex items-center justify-center gap-2 text-sm text-gray-400">
                      <Upload className="h-4 w-4" />
                      <span>{productCurrentPdfUrl ? 'PDF ersetzen' : 'PDF hochladen'}</span>
                    </div>
                  )}
                </div>
                <input
                  ref={pdfFileRef}
                  type="file"
                  accept=".pdf"
                  className="hidden"
                  onChange={(e) => setProductPdfFile(e.target.files?.[0] || null)}
                />
                <p className="text-xs text-gray-400 mt-1">
                  Optional · Produkte mit PDF erscheinen im Handbuch
                </p>
              </div>
            </div>

            {/* ── Vitrinenbild-Bereich ── */}
            <div className="border-t pt-3 space-y-3">
              <div className="flex items-center gap-2 text-sm font-medium text-blue-700 dark:text-blue-400">
                <ImageIcon className="h-4 w-4" />
                Vitrinenbild (optional)
              </div>
              {productCurrentImageUrl && !productImageFile && (
                <div className="flex items-center gap-3 p-2 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                  <img
                    src={`${process.env.REACT_APP_BACKEND_URL}/api/backshop/products/${editingProduct?.id}/image?token=${localStorage.getItem('token')}`}
                    alt="Vitrinenbild"
                    className="h-16 w-16 object-cover rounded border border-blue-200"
                    onError={(e) => { e.target.style.display = 'none'; }}
                  />
                  <div className="flex-1">
                    <p className="text-sm text-blue-700 dark:text-blue-300 font-medium">Bild vorhanden</p>
                    <button
                      type="button"
                      onClick={async () => {
                        try {
                          await axios.delete(`${API}/backshop/products/${editingProduct.id}/image`);
                          setProductCurrentImageUrl(null);
                          toast.success('Bild entfernt');
                          fetchData();
                        } catch (e) {
                          toast.error('Fehler beim Entfernen');
                        }
                      }}
                      className="text-xs text-red-500 hover:text-red-700 mt-1 flex items-center gap-1"
                    >
                      <XIcon className="h-3 w-3" /> Bild löschen
                    </button>
                  </div>
                </div>
              )}
              <div
                className="border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg p-3 text-center cursor-pointer hover:border-blue-400 transition-colors"
                onClick={() => imageFileRef.current?.click()}
              >
                {productImageFile ? (
                  <div className="flex items-center justify-center gap-2 text-sm">
                    <ImageIcon className="h-4 w-4 text-blue-500" />
                    <span className="truncate max-w-[180px]">{productImageFile.name}</span>
                    <button type="button" onClick={(e) => { e.stopPropagation(); setProductImageFile(null); }}>
                      <XIcon className="h-3 w-3 text-gray-400 hover:text-red-500" />
                    </button>
                  </div>
                ) : (
                  <div className="flex items-center justify-center gap-2 text-sm text-gray-400">
                    <Upload className="h-4 w-4" />
                    <span>{productCurrentImageUrl ? 'Bild ersetzen' : 'Bild hochladen'}</span>
                  </div>
                )}
              </div>
              <input
                ref={imageFileRef}
                type="file"
                accept="image/jpeg,image/png,image/webp,.jpg,.jpeg,.png,.webp"
                className="hidden"
                onChange={(e) => setProductImageFile(e.target.files?.[0] || null)}
              />
              <p className="text-xs text-gray-400">
                Optional · JPG/PNG/WEBP · max. 10 MB · Wird im Vitrinen-Baukasten angezeigt
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setProductDialogOpen(false)}>
              Abbrechen
            </Button>
            <Button onClick={() => saveProduct()} disabled={savingProduct} className="bg-orange-600 hover:bg-orange-700">
              {savingProduct ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Speichern...
                </>
              ) : (
                'Speichern'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Product Dialog */}
      <AlertDialog open={!!deleteProductDialog} onOpenChange={() => setDeleteProductDialog(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Produkt löschen?</AlertDialogTitle>
            <AlertDialogDescription>
              Möchten Sie "{deleteProductDialog?.name}" wirklich löschen? 
              Ältere Abschriften bleiben in der Statistik erhalten.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Abbrechen</AlertDialogCancel>
            <AlertDialogAction onClick={deleteProduct} className="bg-red-600 hover:bg-red-700">
              Löschen
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* ── AP3: Duplikat-Dialog (ersetzt alten Gatekeeper) ─────────────── */}
      <BackshopDuplikatDialog
        match={gatekeeperMatch}
        onAccept={handleGatekeeperAccept}
        onEnrichImage={handleGatekeeperEnrichImage}
        onEnrichPdf={handleGatekeeperEnrichPdf}
        onCancel={handleGatekeeperDecline}
        loading={savingProduct}
      />
    </DashboardLayout>
  );
};
